import { gql, useQuery } from '@apollo/client';
import { Alert, CircularProgress, Grid, styled } from '@mui/material';
import { OAuthCallbackRoute, PrivateRoute, useAuthentication } from '@onemedical/auth';
import MicroFrontend from '@onemedical/micro-frontend';
import * as Sentry from '@sentry/react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { useMemo, useRef, useEffect } from 'react';
import isEqual from 'lodash/isEqual';

import mixpanel from '../mixpanel';
import CommandPalette from './CommandPalette';
import NavBar from './NavBar';
import { PendoUserFields, usePendo } from './hooks/Pendo';
import { useLaunchDarkly } from './hooks/LaunchDarkly';
import { InternalUser } from './models/InternalUser';
import { ProfileProvider } from './hooks/useProfile';
import { ProviderStatusProvider } from './hooks/useProviderStatus';
import ObservabilityWrapper from './wrappers/ObservabilityWrapper';

import hostedApps from '../data/hostedApps';

const APP_DATA = gql`
  query AppData {
    profile {
      id
      roles {
        id
        normalizedId
        name
      }
      ...PendoUserFields
      ...NavBarProfile
    }
    zendeskToken
  }
  ${PendoUserFields}
  ${NavBar.fragments.profile}
`;

interface AppData {
  profile: InternalUser;
  zendeskToken: string;
}

const StyledGrid = styled(Grid)(() => ({
  height: '100vh',
  width: '100%',
}));

Sentry.init({
  environment: process.env.REACT_APP_ENVIRONMENT,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [],
});

let mountCount = 0;

function App() {
  mountCount += 1;
  const mountId = useRef(mountCount);
  
  useEffect(() => {
    const id = mountId.current;
    console.log('[App Mount]', { mountId: id, timestamp: new Date().toISOString() }); // eslint-disable-line no-console
    return () => {
      console.log('[App Unmount]', { mountId: id, timestamp: new Date().toISOString() }); // eslint-disable-line no-console
    };
  }, []);
  
  const [, { authenticating, authenticated, error: authError }] = useAuthentication();
  const location = useLocation();
  const launchdarkly = useLaunchDarkly();
  const pendo = usePendo();
  
  // Track what's causing re-renders
  const renderCount = useRef(0);
  const prevLDRef = useRef(launchdarkly);
  const prevPendoRef = useRef(pendo);
  const prevAuthenticatingRef = useRef(authenticating);
  const prevAuthenticatedRef = useRef(authenticated);
  const prevLocationRef = useRef(location);
  
  renderCount.current += 1;
  
  const ldChanged = prevLDRef.current !== launchdarkly;
  const pendoChanged = prevPendoRef.current !== pendo;
  const authenticatingChanged = prevAuthenticatingRef.current !== authenticating;
  const authenticatedChanged = prevAuthenticatedRef.current !== authenticated;
  const locationChanged = prevLocationRef.current !== location;
  
  if (ldChanged || pendoChanged || authenticatingChanged || authenticatedChanged || locationChanged) {
    console.log('[App Render - Change Detected]', { // eslint-disable-line no-console
      renderCount: renderCount.current,
      timestamp: new Date().toISOString(),
      ldChanged,
      pendoChanged,
      authenticatingChanged,
      authenticatedChanged,
      locationChanged,
      pathname: location.pathname,
    });
  }
  
  prevLDRef.current = launchdarkly;
  prevPendoRef.current = pendo;
  prevAuthenticatingRef.current = authenticating;
  prevAuthenticatedRef.current = authenticated;
  prevLocationRef.current = location;
  
  const { data, loading, called, networkStatus } = useQuery<AppData>(APP_DATA, {
    skip: !authenticated,
  });
  
  // Track if query result is causing re-renders
  const prevDataRef = useRef(data);
  const prevLoadingRef = useRef(loading);
  const prevNetworkStatusRef = useRef(networkStatus);
  
  const dataRefChanged = prevDataRef.current !== data;
  const loadingChanged = prevLoadingRef.current !== loading;
  const networkStatusChanged = prevNetworkStatusRef.current !== networkStatus;
  
  if (dataRefChanged || loadingChanged || networkStatusChanged) {
    console.log('[Apollo Query Change]', { // eslint-disable-line no-console
      timestamp: new Date().toISOString(),
      dataRefChanged,
      loadingChanged,
      networkStatusChanged,
      networkStatus,
      profileId: data?.profile?.id,
    });
  }
  
  prevDataRef.current = data;
  prevLoadingRef.current = loading;
  prevNetworkStatusRef.current = networkStatus;
  
  // Log AppData query behavior to debug double execution
  useEffect(() => {
    console.log('[AppData Debug]', { // eslint-disable-line no-console
      timestamp: new Date().toISOString(),
      called,
      loading,
      hasData: !!data,
      networkStatus,
      profileId: data?.profile?.id,
    });
  }, [called, loading, data, networkStatus]);
  
  // Keep stable reference to data when values haven't changed
  // This prevents micro-frontends from re-rendering when Apollo returns
  // new object references with identical values
  const stableDataRef = useRef<AppData | undefined>();
  const stableData = useMemo(() => {
    if (!data) return undefined;
    // Compare only profile data - zendeskToken regenerates but doesn't affect rendering
    const isProfileEqual = isEqual(stableDataRef.current?.profile, data.profile);
    if (isProfileEqual && stableDataRef.current) {
      console.log('[StableData] Profile unchanged, returning cached reference'); // eslint-disable-line no-console
      return stableDataRef.current;
    }
    console.log('[StableData] Profile changed, updating reference'); // eslint-disable-line no-console
    stableDataRef.current = data;
    return data;
  }, [data]);
  
  if (stableData?.profile) {
    launchdarkly.identify(stableData.profile);
    mixpanel.identify(stableData.profile.id);
    pendo.initialize(stableData.zendeskToken, stableData.profile);
  }

  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');

  const showLoadingScreen = authenticating || authError || loading;

  if (showLoadingScreen) {
    return (
      <StyledGrid container>
        <Grid item container justifyContent="center" alignItems="center" direction="column">
          {authError ? (
            <Grid item>
              <Alert severity="error">{authError}</Alert>
            </Grid>
          ) : (
            <>
              <Grid item>
                <CircularProgress />
              </Grid>
              <Grid item>
                <div>{authenticating && 'Checking credentials...'}</div>
              </Grid>
            </>
          )}
        </Grid>
      </StyledGrid>
    );
  }

  return (
    <ObservabilityWrapper>
      <CommandPalette />
      <OAuthCallbackRoute />
      {/* legacy routes used by onelife (to be deleted) */}
      <Route
        path={['/contacts', '/contact', '/provider-directory']}
        render={({ location: loc }) => <Redirect to={`/admin${loc.pathname}${loc.search}`} />}
      />
      <Route
        path={['/appointments', '/phlebotomy-visits', '/provider-schedule']}
        render={({ location: loc }) => (
          <Redirect to={`/schedule${loc.pathname}${loc.search}`} />
        )}
      />
      {/* end of legacy routes */}
      <Route exact path="/">
        {authenticated && <Redirect to="/admin" />}
      </Route>
      <PrivateRoute path="/">
        {stableData?.profile && view !== 'print' && (
          <ProfileProvider>
            <ProviderStatusProvider>
              <NavBar profile={stableData.profile} />{' '}
            </ProviderStatusProvider>
          </ProfileProvider>
        )}
      </PrivateRoute>
      {/* Only render micro-frontends after AppData is loaded */}
      {stableData && hostedApps.map(({ id, path, host }) => (
        <Route
          key={id}
          path={path}
          render={({ history }) => (
            <MicroFrontend
              id={id}
              host={host}
              history={history}
              containedAppProps={stableData} // used to trigger a refresh on profile change
              onError={(error) => {
                console.error(`Failed to load '${id}' micro-frontend`, error); // eslint-disable-line no-console
              }}
            >
              <StyledGrid container justifyContent="center" alignItems="center" direction="column">
                <Grid item>
                  <CircularProgress />
                </Grid>
              </StyledGrid>
              {Sentry.setTag('container_frontend', id)}
            </MicroFrontend>
          )}
        />
      ))}
    </ObservabilityWrapper>
  );
}

export default App;
